import { FC, useEffect, useState } from 'react'
import { TaskStatusCell, EmailIconCell } from 'shared/molecules/table/cells'
import { IFilter, Operator } from 'api/types'
import { useTableHandlers } from 'hooks/useTableHandlers'
import {
  Table,
  Toolbar,
  Modal,
  Button,
  BreadcrumHeader,
  DOCUMENT_SCAN_STAGES
} from 'shared'
import { BreadcrumbItemType } from 'shared/molecules/Breadcrum/Breadcrum'
import { notifyError, notifySuccess } from 'utils'
import { Checkbox, Loader } from 'rsuite'
import CopyOnHoverCell from 'shared/molecules/table/cells/CopyCell'
import ClientTaskInfo from './ClientTaskInfo'
import useAuth from '../../../hooks/Auth'
import {
  useGetClientTaskQuery,
  useCreateDownloadReportMutation,
  useCreateRedoMutation
} from '../clientApiSlice'
import { Client, ClientListRequest } from '../types'
import '../client.scss'

const { Column, HeaderCell, Cell, ActionCell, DateTimeCell } = Table

const ClientTaskList: FC = () => {
  const [confirmRedo, setConfirmRedo] = useState<boolean>(false)
  const [taskLog, setTaskLog] = useState<boolean>(false)
  const [selectedTask, setSelectedTask] = useState<Client | null>(null)
  const [taskInfoData, settaskInfoData] = useState<Client | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [isChecked, setIsChecked] = useState<boolean>(false)
  const [isNotId, setIsNotId] = useState<boolean>(false)
  const { selectedClientId } = useAuth()

  const id = selectedClientId ?? ''

  useEffect(() => {
    if (!id) {
      setIsNotId(true)
    }
  }, [id])

  const { requestBody, onPageChange, onFilterChange, onSearchChange } = useTableHandlers<
    Client,
    ClientListRequest
  >({
    filters: [],
    page: { size: 10, number: 0 },
    id
  })

  const {
    data: ClientTask,
    isLoading,
    isFetching,
    refetch
  } = useGetClientTaskQuery(requestBody, { skip: !id })

  useEffect(() => {
    if (id) {
      refetch()
    }
  }, [id, refetch])

  const [createDownloadReport] = useCreateDownloadReportMutation()

  const [createRedo] = useCreateRedoMutation()
  const handleTabChange = () => {}
  const getFileExtension = (contentType?: string): string => {
    if (!contentType) {
      return 'dat'
    }
    if (contentType.includes('pdf')) return 'pdf'
    if (contentType.includes('csv')) return 'csv'
    if (contentType.includes('spreadsheetml')) return 'xlsx'
    if (contentType.includes('octet-stream')) return 'bin'
    return 'dat'
  }

  const breadcrumbItems: BreadcrumbItemType[] = [
    { label: 'Client', path: '/client' },
    { label: 'Task', isActive: true }
  ]

  const RedoConfirmation = async () => {
    const taskId = selectedTask?.taskId
    if (!taskId) {
      notifyError('Failed to Retrained task. Please try again.', {
        containerId: 'modalToast'
      })
      return
    }
    try {
      setLoading(true)
      await createRedo({ id: taskId })
      setConfirmRedo(false)
      setSelectedTask(null)
      setIsChecked(false)
      setLoading(false)
      notifySuccess('Document Trained Successfully')
    } catch (error) {
      notifyError('Failed to Retrained task. Please try again.', {
        containerId: 'modalToast'
      })
      setLoading(false)
    }
  }

  const handleDownloadPdf = async (rowData: Client) => {
    if (!rowData?.taskId) {
      notifyError('Failed to Download Pdf', { containerId: 'modalToast' })
      return
    }
    try {
      const response = await createDownloadReport({
        id: rowData.taskId
      }).unwrap()
      const fileExtension = getFileExtension(response?.type)
      const fileName = `exceleris Report.${fileExtension}`
      const fileURL = URL.createObjectURL(response)
      const a = document.createElement('a')
      a.href = fileURL
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(fileURL)
      notifySuccess('Pdf Download Successfully')
    } catch (error) {
      notifyError('Failed to download Pdf. Please try again.')
    }
  }

  const handleClose = () => {
    setTaskLog(false)
  }

  const handleAction = async (action: string | undefined, rowData: Client) => {
    if (!rowData) {
      return
    }

    switch (action) {
      case '7': {
        handleDownloadPdf(rowData)
        break
      }
      case '5': {
        setTaskLog(true)
        settaskInfoData(rowData)
        break
      }
      case '9': {
        setSelectedTask(rowData)
        setConfirmRedo(true)
        break
      }
      default:
        break
    }
  }

  const options = [
    {
      label: 'All',
      value: 'All',
      onClick: () => handleTabChange()
    }
  ]

  const dropdownOptions = [
    { label: 'Pending', value: 'PENDING' },
    { label: 'Failed', value: 'FAILED' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Raw Response Saved', value: 'RAW_RESPONSE_SAVED' },
    { label: 'Untrained Document', value: 'UNTRAINED_DOCUMENT' }
  ]

  const handleDropdown = (value: string) => {
    const compaignFilter: IFilter<Client> = {
      fieldName: 'status',
      operator: Operator.EQ,
      fieldValue: value ?? ''
    }

    const updatedFilters = [
      compaignFilter,
      ...(requestBody.filters ?? []).filter((f) => f.fieldName !== 'status')
    ]
    onFilterChange(updatedFilters)
  }

  return (
    <div className="list">
      <BreadcrumHeader items={breadcrumbItems} />
      <Toolbar
        options={options}
        total={id ? (ClientTask?.totalElements ?? 0) : 0}
        onDropdownSelect={handleDropdown}
        dropdownPlaceholder="Select Task Status"
        dropdownData={dropdownOptions}
        onSearchChange={onSearchChange}
      />

      <div className="list__main-container">
        <Table
          data={id ? (ClientTask?.content ?? []) : []}
          loading={isLoading || isFetching}
          loadingStages={DOCUMENT_SCAN_STAGES}
          paginated
          pageSizeOptions={[10, 20, 30]}
          total={id ? (ClientTask?.totalElements ?? 0) : 0}
          defaultPageSize={10}
          onPageChange={onPageChange}
          height={600}
          rowHeight={50}
          virtualized
        >
          <Column flexGrow={1} minWidth={150} key="taskId" align="left" sortable>
            <HeaderCell>Task ID</HeaderCell>
            <CopyOnHoverCell dataKey="taskId" />
          </Column>

          <Column width={60} align="center">
            <HeaderCell>📧</HeaderCell>
            <EmailIconCell dataKey="emailId" />
          </Column>

          <Column flexGrow={2} minWidth={200} align="left" sortable>
            <HeaderCell>Subject</HeaderCell>
            <Cell dataKey="subject" tooltip />
          </Column>

          <Column flexGrow={1.5} minWidth={180} align="left" sortable>
            <HeaderCell>File Name</HeaderCell>
            <Cell dataKey="fileName" tooltip />
          </Column>

          <Column flexGrow={1.2} minWidth={160} align="left" sortable>
            <HeaderCell>Received At</HeaderCell>
            <DateTimeCell
              dataKey="emailsReceivedAt"
              format="dd/MMM/yy, h:mm a"
            />
          </Column>

          <Column width={130} align="center">
            <HeaderCell>Status</HeaderCell>
            <TaskStatusCell dataKey="status" />
          </Column>

          <Column width={100} align="center">
            <HeaderCell>Actions</HeaderCell>
            <ActionCell
              tooltip
              dataKey="action"
              onAction={handleAction}
              actionOptions={['Download Pdf', 'Task Log', 'Redo']}
              disableEdit
            />
          </Column>
        </Table>
      </div>
      {confirmRedo && (
        <Modal
          open={confirmRedo}
          onClose={() => {
            setConfirmRedo(false)
            setSelectedTask(null)
            setIsChecked(false)
          }}
          size="sm"
          dialogClassName="redo-confirmation-modal"
          title="Confirm Reprocessing"
          body={
            <div className="modal-content">
              <p className="modal-message">
                Are you sure you want to reprocess this document?
              </p>
              <div className="modal-checkbox">
                <Checkbox
                  checked={isChecked}
                  onChange={(_, checked) => setIsChecked(checked)}
                >
                  I confirm that the document has been trained in Document AI
                </Checkbox>
              </div>
            </div>
          }
          footer={
            <div className="modal-footer-buttons">
              <Button
                onClick={() => {
                  setConfirmRedo(false)
                  setSelectedTask(null)
                  setIsChecked(false)
                }}
                appearance="subtle"
              >
                Cancel
              </Button>
              <Button
                onClick={() => RedoConfirmation()}
                appearance="primary"
                disabled={!isChecked}
              >
                {loading ? (
                  <>
                    Processing... <Loader size="xs" inverse />
                  </>
                ) : (
                  'Confirm'
                )}
              </Button>
            </div>
          }
        />
      )}
      {isNotId && (
        <Modal
          open={isNotId}
          title=""
          onClose={() => setIsNotId(false)}
          size="calc(40% - 120px)"
          body={
            <>
              <p className="modal-title">
                Please Select the Client First From Header{' '}
              </p>
              <div className="modal-btn">
                <Button
                  className="primary btn"
                  onClick={() => {
                    setIsNotId(false)
                  }}
                >
                  Ok
                </Button>
              </div>
            </>
          }
        />
      )}

      {taskLog && (
        <ClientTaskInfo
          open={taskLog}
          onClose={handleClose}
          taskData={taskInfoData}
        />
      )}
    </div>
  )
}
export default ClientTaskList